import React from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Avatar,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Grid,
  Button,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { VpnKey } from "@mui/icons-material";
import { Search, X } from "lucide-react";
import { GlassDialog, dialogTransitionProps } from "../../../theme/theme";

export function LicenseDialog({
  open,
  onClose,
  theme,
  darkMode,
  systemSearch,
  setSystemSearch,
  newLicense,
  setNewLicense,
  addLicense,
}) {
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <GlassDialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen} {...dialogTransitionProps}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), width: 48, height: 48 }}>
            <VpnKey color="primary" />
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
              Add New License
            </Typography>
            <Typography variant="caption" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
              Create a new software license for a system
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Box>
          <TextField
            fullWidth
            margin="normal"
            label="Search System"
            placeholder="Type UUID, project name, or software..."
            value={systemSearch}
            onChange={(e) => { setSystemSearch(e.target.value); setNewLicense(prev => ({ ...prev, uuid: '' })); }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search size={18} /></InputAdornment>,
              endAdornment: systemSearch && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSystemSearch(''); setNewLicense({ ...newLicense, uuid: '' }); }}>
                    <X size={16} />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          {systemSearch && !newLicense.uuid && (
            <Paper variant="outlined" sx={{ maxHeight: 200, overflowY: 'auto', mt: -1, mb: 1, borderRadius: 2 }}>
              {newLicense.systemOptions
                .filter(opt =>
                  opt.value.toLowerCase().includes(systemSearch.toLowerCase()) ||
                  opt.project_id.toLowerCase().includes(systemSearch.toLowerCase()) ||
                  opt.software_id.toLowerCase().includes(systemSearch.toLowerCase())
                )
                .slice(0, 8)
                .map(opt => (
                  <Box
                    key={opt.value}
                    onClick={() => {
                      setNewLicense({ ...newLicense, uuid: opt.value });
                      setSystemSearch(opt.displayText);
                    }}
                    sx={{
                      px: 2, py: 1.5, cursor: 'pointer',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                      bgcolor: newLicense.uuid === opt.value ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {opt.value.substring(0, 16)}...
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {opt.project_id} • {opt.software_id}
                    </Typography>
                  </Box>
                ))}
              {newLicense.systemOptions.filter(opt =>
                opt.value.toLowerCase().includes(systemSearch.toLowerCase()) ||
                opt.project_id.toLowerCase().includes(systemSearch.toLowerCase()) ||
                opt.software_id.toLowerCase().includes(systemSearch.toLowerCase())
              ).length === 0 && (
                <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No systems found</Typography>
                </Box>
              )}
            </Paper>
          )}
          {newLicense.uuid && (
            <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2, mb: 1, border: '1px solid', borderColor: alpha(theme.palette.primary.main, 0.2) }}>
              <Typography variant="caption" color="text.secondary">Selected:</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{newLicense.uuid}</Typography>
            </Box>
          )}
        </Box>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={newLicense.start_date}
              onChange={(e) => setNewLicense({ ...newLicense, start_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              margin="normal"
              InputLabelProps={{ shrink: true }}
              value={newLicense.end_date}
              onChange={(e) => setNewLicense({ ...newLicense, end_date: e.target.value })}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={addLicense} variant="contained" sx={{ px: 4 }}>
          Create License
        </Button>
      </DialogActions>
    </GlassDialog>
  );
}

export default LicenseDialog;
